import React, { useContext, useState, useEffect, Fragment } from 'react'
import { MarkupContext } from '../../providers/Markup'
import ButtonEl from '../helpers/ctas/Button'
import { ChevronDownIcon, EllipsisVerticalIcon, ArrowLongUpIcon, ArrowRightIcon } from '@heroicons/react/24/solid'

// Controls whether the CTA trigger button overrides ButtonEl's base palette styles.
// When true, the trigger is expected to inherit colours from its parent container
// (e.g. the header background) rather than the button token palette.
// NOTE: ButtonEl does not currently consume this prop — if overrideBase behaviour is
// needed, it must be implemented in Button.jsx. This const is declared here so the
// intent is easy to find and wire up when required.
const TRIGGER_OVERRIDE_BASE = true

function CtaDropdownTriggerButton(props) {
  const { detectPaletteClass } = useContext(MarkupContext)
  return (
    <ButtonEl
      key="header-cta-nav-0-button"
      theme={props.theme}
      // added workaround to get rounded working correctly here.
      icon={props.ctas[0].linkIconClass}
      position={props.ctas[0].linkPosition}
      onClick={props.onClick}
      ariaHasPopup="true"
      ariaControls="navCtaWrapper"
      ariaExpanded={`${props.ctaActive}`}
      ariaHidden="false"
      overrideBase={TRIGGER_OVERRIDE_BASE}
      iconClass={`${props.iconClass ? props.iconClass : ''}`}
      className={`flex items-center justify-center flex-row flex-nowrap gap-2 h-full !min-w-max w-full cursor-pointer outline-none
          hover:underline hover:decoration-4 hover:decoration-white
        [&_[data-type=button-decorator]]:hidden
        ${props.ctaActive === 'true' ? 'underline decoration-4 decoration-white' : ''}`}
    >
      <div className="whitespace-nowrap shrink-0 text-sm font-medium leading-none pr-1">{props.ctas[0].link?.name}</div>
      <ChevronDownIcon
        className={detectPaletteClass(
          'h-4 w-4 shrink-0 text-primary-500-text group-[.active]/ctawrapper:-scale-y-100 group-[.inactive]/ctawrapper:scale-y-100 transition-all duration-200',
        )}
        role="none"
      />
    </ButtonEl>
  )
}

function CtaDropdownTriggerMinimal(props) {
  return (
    <ButtonEl
      key="header-cta-nav-0-button"
      theme={props.theme}
      // added workaround to get rounded working correctly here.
      // icon={props.ctas[0].icon}
      onClick={props.onClick}
      ariaHasPopup="true"
      ariaControls="navCtaWrapper"
      ariaExpanded={`${props.ctaActive}`}
      ariaHidden="false"
    >
      <span className="sr-only">{props.ctas[0].name}</span>
      <div className="h-4 w-4 relative">
        <EllipsisVerticalIcon
          className="absolute top-0 left-0 h-4 w-4 group-[.active]/ctawrapper:opacity-0 group-[.inactive]/ctawrapper:opacity-100 transition-all duration-1000"
          role="none"
        />
        <ArrowLongUpIcon
          className="absolute top-0 left-0 h-4 w-4 group-[.active]/ctawrapper:opacity-100 group-[.inactive]/ctawrapper:opacity-0 transition-all duration-1000"
          role="none"
        />
      </div>
    </ButtonEl>
  )
}

function CtaDropDownTrigger(props) {
  if (props.triggerStyle === 'minimal') {
    // returns the minimal trigger button.
    return <CtaDropdownTriggerMinimal {...props} theme="none" />
  } else if (props.triggerStyle === 'button') {
    // inherits the button style from the first CTA as defined in the CMS
    return <CtaDropdownTriggerButton {...props} theme={`${props.ctas[0].linkType}`} />
  }
  // this overrides any theme set on the first button to show the "standard" version of the dropdown.
  return <CtaDropdownTriggerButton {...props} theme="none" />
}

function CtaDropdown(props) {
  const [ctaActive, setCtaActive] = useState(false)
  let variant = ['simple', 'icon', 'divider'].includes(props.variant) ? props.variant : 'simple'
  const [variantClasses, setVariantClasses] = useState('')
  const wrapperClasses =
    props.position === 'mobile'
      ? 'ml-3 block md:hidden'
      : props.position === 'mobileNav'
        ? 'block w-full'
        : 'hidden md:block shrink-0'
  const menuClasses =
    props.position === 'mobile' || props.position === 'mobileNav'
      ? 'origin-bottom translate-y-[-110%] top-0'
      : 'origin-top'
  const dropdownWidthClass = props.position === 'mobileNav' ? 'w-full' : 'min-w-[224px]'

  useEffect(() => {
    setCtaActive((prevState) => (props.menuActive === true || props.searchActive === true ? false : prevState))
  }, [props.menuActive, props.searchActive])

  // Attach click-outside handler once only, with proper cleanup to prevent listener accumulation
  useEffect(() => {
    const handleClickOutside = (evt) => {
      if (!evt.target.closest('[data-type="cta-dropdown"]')) {
        setCtaActive(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    setVariantClasses((dd) => {
      if (props.variant === 'divider') {
        return 'divide-y'
      }
      return ''
    })
  }, [props.variant])

  const handleCtaExpandClick = () => {
    setCtaActive((prevState) => {
      const isActive = !prevState
      if (props.position !== 'mobile' && props.position !== 'mobileNav') {
        props.setCtasActive(isActive)
        props.setSearchActive((prevState) => {
          let newState = prevState
          if (isActive === true) {
            // is search has become active turn the menu off
            newState = false
          }
          // otherwise do nothing.
          props.setMenuActive(newState)
          return newState
        })
      }
      return isActive
    })
  }

  return (
    <div data-type="" className={`${wrapperClasses} ${props.className}`}>
      {props.ctas?.length > 1 && (
        // <p>Multiple Buttons</p>
        // we need to build a drop-down from this, using the first button as a label only
        // any links added to this CTA will be ignored.
        <nav
          data-type="cta-dropdown"
          aria-label="Main CTA navigation"
          className={`group/ctawrapper relative h-full ${ctaActive ? 'active' : 'inactive'}`}
        >
          <ul className="h-full">
            <li key="header-cta-nav-0" className={`has-submenu h-full`}>
              <CtaDropDownTrigger
                {...props}
                ctaActive={`${ctaActive}`}
                onClick={handleCtaExpandClick}
                triggerStyle={props.type}
              />
              <ul
                id="navCtaWrapper"
                className={`${ctaActive ? 'scale-y-100 pointer-events-auto' : 'scale-y-0 pointer-events-none'} ${menuClasses} ${dropdownWidthClass} shadow-xl transition-all duration-200 absolute top-full z-50 bg-white rounded-b-lg ${variantClasses} right-0`}
              >
                {props.ctas.map((cta, idx) => {
                  if (idx == 0) {
                    return
                  }
                  return (
                    <Fragment key={`header-cta-nav-${idx}`}>
                      {idx > 0 && (
                        <li className={`my-1`}>
                          <ButtonEl
                            theme={'none'}
                            link={cta.link}
                            active={`${ctaActive}`}
                            className={`${props.variant === 'icon' ? 'w-full content-between ' : ''} text-slate-800`}
                            ariaHidden={`${ctaActive ? 'false' : 'true'}`}
                            overrideBase={props.overrideBase}
                          >
                            {props.variant !== 'icon' && <span>{cta.link.name}</span>}
                            {props.variant === 'icon' && (
                              <>
                                <div className="basis-3/4">{cta.link.name}</div>
                                <ArrowRightIcon className="w-6 h-4 basis-1/4 shrink-0" />
                              </>
                            )}
                          </ButtonEl>
                        </li>
                      )}
                    </Fragment>
                  )
                })}
              </ul>
            </li>
          </ul>
        </nav>
      )}

      {props.ctas?.length === 1 &&
        props.ctas.map((cta, i) => {
          // return <p>Button</p>
          return (
            <ButtonEl
              overrideBase={props.overrideBase}
              key={i}
              theme={cta.linkType}
              link={cta.link}
              icon={cta.linkIconClass}
              position={cta.linkPosition}
              iconClass={`shrink-0 ${props.iconClass ? props.iconClass : ''}`}
              className="flex items-center justify-center flex-row flex-nowrap gap-2 h-full !min-w-max whitespace-nowrap"
            />
          )
        })}
    </div>
  )
}

export default CtaDropdown
